"use client";
import { useTranslations } from "next-intl";
import { useState, useMemo } from "react";
import clsx from "clsx";
import { Link } from "@/navigation";
import Image from "next/image";
import { getLocalizedHengSpecDetail } from "@/lib/utils/productMetadata";

const getApplicationDescriptionText = (value: unknown) => {
	if (typeof value !== "string") return "";

	return value
		.replace(/<br\s*\/?>/gi, "\n")
		.replace(/<\/p>/gi, "\n\n")
		.replace(/<[^>]*>/g, " ")
		.replace(/[^\S\r\n]+/g, " ")
		.replace(/\n[^\S\r\n]+/g, "\n")
		.replace(/[^\S\r\n]+\n/g, "\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
};

const ProductDescription = ({ product, locale }) => {
	const t = useTranslations("nav");
	const [expandedTabs, setExpandedTabs] = useState<boolean[]>([true, true, true]);
	const localizedHengSpecDetail = useMemo(
		() => getLocalizedHengSpecDetail(product?.metadata, locale),
		[product?.metadata, locale],
	);

	// 从本地化 hengSpecDetail 中获取并格式化系列优势。
	const seriesAdvantages = useMemo(() => {
		const productRange = localizedHengSpecDetail?.productRange;
		if (!productRange || productRange.length === 0) return '';

		const seriesAdvantagesItem = productRange.find((item) =>
			item.title === 'Series Advantages' || item.title === '系列优势'
		);
		if (seriesAdvantagesItem?.description) {
			return seriesAdvantagesItem.description.replace(/\n/g, '<br/>');
		}

		let htmlContent = '';
		productRange.forEach((item, index) => {
			if (item.description) {
				if (item.title) {
					htmlContent += `<div class="mb-4"><h3 class="font-semibold text-lg mb-2">${item.title}</h3><div>${item.description}</div></div>`;
				} else {
					htmlContent += `<div class="mb-4">${item.description}</div>`;
				}
			}
			if (index < productRange.length - 1) {
				htmlContent += '<hr class="my-6 border-gray-300"/>';
			}
		});

		return htmlContent.replace(/\n/g, '<br/>');
	}, [localizedHengSpecDetail]);

	// 获取商品描述内容（技术规格）
	const technicalSpecifications = useMemo(() => {
		// 优先使用翻译版本的描述
		const descriptionJson = product?.translation?.descriptionJson || product?.descriptionJson;
		
		if (descriptionJson) {
			try {
				const parsed = filterSortDsc(descriptionJson);
				// 返回长描述（blocks[0]），这是完整的产品描述
				return parsed.longDsc || '';
			} catch (e) {
				console.error('Failed to parse product description:', e);
				return '';
			}
		}
		
		return '';
	}, [product?.translation?.descriptionJson, product?.descriptionJson, locale]);

	// 从同一份本地化 hengSpecDetail 中获取应用行业。
	const applicationIndustries = useMemo(() => {
		const usageScenarios = localizedHengSpecDetail?.usageScenarios;
		return usageScenarios && usageScenarios.length > 0 ? usageScenarios : null;
	}, [localizedHengSpecDetail]);

	return (
		<div className="w-full">
			{/* 控制按钮 */}
			<div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 mb-6">
				<button
					onClick={() => setExpandedTabs([true, true, true])}
					className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-mainColor text-mainColor hover:bg-mainColor hover:text-white transition-all duration-200 rounded text-sm sm:text-base"
				>
					<span>{t("Open-All")}</span>
					<i className="ri-arrow-down-s-line"></i>
				</button>
				<div className="hidden sm:block w-px h-6 bg-gray-300"></div>
				<button
					onClick={() => setExpandedTabs([false, false, false])}
					className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-mainColor text-mainColor hover:bg-mainColor hover:text-white transition-all duration-200 rounded text-sm sm:text-base"
				>
					<span>{t("Close-All")}</span>
					<i className="ri-arrow-up-s-line"></i>
				</button>
			</div>

			{/* Tab 1: 系列优势 (Series Advantages) */}
			<div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
				<div 
					className={clsx(
						"flex items-center justify-between cursor-pointer py-4 px-5 transition-colors duration-200",
						expandedTabs[0] 
							? "bg-mainColor text-white" 
							: "bg-white hover:bg-gray-50"
					)}
					onClick={() => setExpandedTabs(prev => [!prev[0], prev[1], prev[2]])}
				>
					<div className="flex items-center gap-3">
						<i className={clsx(
							"ri-star-line text-xl transition-colors duration-200",
							expandedTabs[0] ? "text-white" : "text-mainColor"
						)}></i>
						<h2 className={clsx(
							"text-lg max-md:text-base font-bold transition-colors duration-200",
							expandedTabs[0] ? "text-white" : "text-gray-800"
						)}>{t("Series-Advantages")}</h2>
					</div>
					<i className={clsx(
						"ri-arrow-down-s-line text-xl transition-all duration-300",
						expandedTabs[0] 
							? "rotate-180 text-white" 
							: "text-mainColor"
					)}></i>
				</div>
				<div 
					className={clsx(
						"overflow-hidden transition-all duration-500 ease-in-out",
						expandedTabs[0] ? "max-h-[10000px] opacity-100" : "max-h-0 opacity-0"
					)}
				>
					<div className="p-5">
						<div
							className="text-gray-700 leading-relaxed text-base"
							dangerouslySetInnerHTML={{ __html: seriesAdvantages || t("No-Content-Available") }}
						/>
					</div>
				</div>
			</div>

			{/* Tab 2: 技术规格 (Technical Specifications) */}
			<div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
				<div 
					className={clsx(
						"flex items-center justify-between cursor-pointer py-4 px-5 transition-colors duration-200",
						expandedTabs[1] 
							? "bg-mainColor text-white" 
							: "bg-white hover:bg-gray-50"
					)}
					onClick={() => setExpandedTabs(prev => [prev[0], !prev[1], prev[2]])}
				>
					<div className="flex items-center gap-3">
						<i className={clsx(
							"ri-file-list-3-line text-xl transition-colors duration-200",
							expandedTabs[1] ? "text-white" : "text-mainColor"
						)}></i>
						<h2 className={clsx(
							"text-lg max-md:text-base font-bold transition-colors duration-200",
							expandedTabs[1] ? "text-white" : "text-gray-800"
						)}>{t("Technical-Specifications")}</h2>
					</div>
					<i className={clsx(
						"ri-arrow-down-s-line text-xl transition-all duration-300",
						expandedTabs[1] 
							? "rotate-180 text-white" 
							: "text-mainColor"
					)}></i>
				</div>
				<div 
					className={clsx(
						"overflow-hidden transition-all duration-500 ease-in-out",
						expandedTabs[1] ? "max-h-[10000px] opacity-100" : "max-h-0 opacity-0"
					)}
				>
					<div className="p-5">
						{technicalSpecifications ? (
							<div 
								className="text-gray-700 leading-relaxed text-base [&_table]:w-full [&_table]:border-collapse [&_table]:border [&_table]:border-black [&_table]:my-4 [&_table]:shadow-sm [&_th]:border [&_th]:border-black [&_th]:px-4 [&_th]:py-3 [&_th]:bg-mainColor/10 [&_th]:font-semibold [&_th]:text-center [&_th]:text-gray-800 [&_td]:border [&_td]:border-black [&_td]:px-4 [&_td]:py-3 [&_td]:text-center [&_td]:text-gray-700 [&_tr:hover]:bg-gray-50"
								dangerouslySetInnerHTML={{ __html: technicalSpecifications }}
							/>
						) : (
							<div className="text-gray-500">{t("No-Content-Available")}</div>
						)}
					</div>
				</div>
			</div>

			{/* Tab 3: 应用行业 (Application Industries) */}
			<div className="border border-gray-200 rounded-lg overflow-hidden">
				<div 
					className={clsx(
						"flex items-center justify-between cursor-pointer py-4 px-5 transition-colors duration-200",
						expandedTabs[2] 
							? "bg-mainColor text-white" 
							: "bg-white hover:bg-gray-50"
					)}
					onClick={() => setExpandedTabs(prev => [prev[0], prev[1], !prev[2]])}
				>
					<div className="flex items-center gap-3">
						<i className={clsx(
							"ri-building-line text-xl transition-colors duration-200",
							expandedTabs[2] ? "text-white" : "text-mainColor"
						)}></i>
						<h2 className={clsx(
							"text-lg max-md:text-base font-bold transition-colors duration-200",
							expandedTabs[2] ? "text-white" : "text-gray-800"
						)}>{t("Application-Industries")}</h2>
					</div>
					<i className={clsx(
						"ri-arrow-down-s-line text-xl transition-all duration-300",
						expandedTabs[2] 
							? "rotate-180 text-white" 
							: "text-mainColor"
					)}></i>
				</div>
				<div 
					className={clsx(
						"overflow-hidden transition-all duration-500 ease-in-out",
						expandedTabs[2] ? "max-h-[10000px] opacity-100" : "max-h-0 opacity-0"
					)}
				>
					<div className="p-5">
						{applicationIndustries && applicationIndustries.length > 0 ? (
							<div className="grid grid-cols-2 gap-4">
								{applicationIndustries.map((item: any, index: number) => {
									const linkUrl = typeof item.link === "string" ? item.link.trim() : "";
									const hasLink = Boolean(linkUrl);
									const descriptionText = getApplicationDescriptionText(item.description);
									const cardClassName = clsx(
										"group flex h-full flex-col overflow-hidden border border-gray-300 transition-all duration-300 hover:border-mainColor hover:shadow-lg",
										hasLink ? "cursor-pointer" : "cursor-default"
									);
									const cardContent = (
										<>
											{/* 图片在上 - 长方形 */}
											{item.media && Array.isArray(item.media) && item.media.length > 0 && item.media[0].url && (
												<div className="w-full aspect-[4/3] overflow-hidden bg-gray-50 flex items-center justify-center transition-transform duration-300">
													<Image
														src={item.media[0].url}
														alt={item.media[0].alt || item.title || ""}
														width={400}
														height={300}
														className="w-full h-full object-cover"
													/>
												</div>
											)}

											{(item.title || descriptionText) && (
												<div className="flex w-full flex-1 flex-col items-start justify-start gap-2 p-4 text-left transition-all duration-300 ">
													{item.title && (
														<div className="text-lg font-semibold text-gray-700 transition-colors duration-300 ">
															{item.title}
														</div>
													)}

													{descriptionText && (
														<div className="whitespace-pre-line text-sm leading-6 text-gray-500 transition-colors duration-300 0">
															{descriptionText}
														</div>
													)}
												</div>
											)}
										</>
									);
									
									return hasLink ? (
										<Link
											key={index}
											href={linkUrl}
											className={cardClassName}
										>
											{cardContent}
										</Link>
									) : (
										<div
											key={index}
											className={cardClassName}
										>
											{cardContent}
										</div>
									);
								})}
							</div>
						) : (
							<div className="text-gray-500">{t("No-Content-Available")}</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductDescription;

type dscList = {
	blocks: dscObj[];
};
type dscObj = {
	data: { text: string };
	type: string;
};
const filterSortDsc = (dsc: string) => {
	if (!dsc) {
		dsc = '{"blocks": []}';
	}

	const dscObj = JSON.parse(dsc) as dscList;
	let sortDsc = "";
	let longDsc = "";

	// 打印后立即检查blocks是否存在
	// console.log(dscObj, 'dscObj');

	// 首先检查blocks是否存在
	if (!dscObj.blocks) {
		// 如果blocks不存在，初始化为空数组
		dscObj.blocks = [];
	}

	// 现在可以安全地访问length属性
	if (dscObj.blocks.length > 0) {
		// 检查索引1是否存在
		if (dscObj.blocks.length > 1 && dscObj.blocks[1]?.data?.text) {
			sortDsc = dscObj.blocks[1].data.text;
		}

		// 检查索引0是否存在
		if (dscObj.blocks[0]?.data?.text) {
			longDsc = handlerInnerHtml(dscObj.blocks[0].data.text);
		}
	}

	return { sortDsc, longDsc };
};
const handlerInnerHtml = (html: string): string => {
	if (!html) return html;
	return html
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&amp;/g, "&")
		.replace(/&quot;/g, '"')
		.replace(/&#8216;/g, "‘")
		.replace(/&#8217;/g, "’")
		.replace(/&#8211;/g, "–")
		.replace(/&nbsp;/g, " ");
};
